import { jsonForm } from '@europrocurement/flexy-form';
import { useFormulaireService } from '@europrocurement/l2d-domain';
import { castIriToId } from '@europrocurement/l2d-utils';
import React, { createContext, PropsWithChildren, useContext, useEffect, useReducer } from 'react';
import { DataProviderProps, DataState } from './types';

type Formulaire = {
    id: number;
    label: string;
    formulaire?: jsonForm;
    formaliteIds: Array<number>;
};

type FormulaireState = DataState & {
    data: Array<Formulaire>;
};

const INIT_ITEMS_CTX = 'init_formulaires_ctx';

type Action = { type: 'init_formulaires_ctx'; payload: Array<Formulaire> };

export const FormulairesContext = createContext<FormulaireState | null>(null);
export const FormulairesDispatchContext = createContext<React.Dispatch<Action> | null>(null);

function formulairesReducer(state: FormulaireState, action: Action) {
    switch (action.type) {
        case INIT_ITEMS_CTX: {
            return {
                loaded: true,
                data: [...action.payload],
            };
        }
        default: {
            throw new Error(`Unknown action: ${action.type}`);
        }
    }
}

export const FormulairesProvider: React.FunctionComponent<PropsWithChildren<DataProviderProps>> =
    function ({ autoload, children }) {
        const [formulaires, dispatch] = useReducer(formulairesReducer, { loaded: false, data: [] });
        const { formulairesApi } = useFormulaireService();

        useEffect(() => {
            if (!autoload) {
                return;
            }

            formulairesApi
                .apiFormulaireFormalitesGetCollection({ itemsPerPage: 999 })
                .then((response) => {
                    dispatch({
                        type: INIT_ITEMS_CTX,
                        payload: response.data['hydra:member'].map((item) => ({
                            id: item.id as number,
                            label: item.libelle as string,
                            formulaire: item.formulaire ? JSON.parse(item.formulaire) : null,
                            formaliteIds: (item.formalites || []).map((formaliteIri) =>
                                castIriToId(formaliteIri),
                            ),
                        })),
                    });
                })
                .catch((e) => console.error(e));
        }, [formulairesApi, autoload]);

        return (
            <FormulairesContext.Provider value={formulaires}>
                <FormulairesDispatchContext.Provider value={dispatch}>
                    {children}
                </FormulairesDispatchContext.Provider>
            </FormulairesContext.Provider>
        );
    };

export function useFormulairesCtx() {
    const state = useContext(FormulairesContext);

    const isDataLoaded = (): boolean => state?.loaded || false;

    const getFormulaires = (): Array<Formulaire> => state?.data || [];

    const getFormulaireById = (id: number): Formulaire | undefined =>
        (state?.data || []).find((formality) => formality.id === id);

    const getFormulairesFiltered = (
        filters: Partial<Record<keyof Formulaire, unknown>>,
    ): Array<Formulaire> =>
        (state?.data || []).filter((item) =>
            Object.entries(filters).every(
                ([key, value]) => item[key as keyof Formulaire] === value,
            ),
        );

    const getFormulaireFiltered = (
        filters: Partial<Record<keyof Formulaire, unknown>>,
    ): Formulaire | undefined => getFormulairesFiltered(filters)[0] || undefined;

    return {
        isDataLoaded,
        getFormulaires,
        getFormulaireById,
        getFormulairesFiltered,
        getFormulaireFiltered,
    };
}

export function useFormulaireCtxDispatch() {
    return useContext(FormulairesDispatchContext);
}
